// PanelModal.tsx
import { Modal, Form, Input, Button, Space, Select, Divider } from 'antd';
import { useSuperAdmin } from '../../../contexts/SuperAdminContext';
import { useEffect, useState } from 'react';
import { getAndSanitize } from '../../../shared/utils/sanitization';

interface PanelFormValues {
  name: string;
  username: string;
  password?: string;
  baseUrl: string;
  admins: { userId: string }[];
}

interface PanelModalProps {
  open: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
  initialData?: {
    id: string;
    name: string;
    username: string;
    baseUrl: string;
    userId?: string; // Campo antiguo (opcional)
    users?: { userId: string; isAdmin?: boolean }[]; // Campo nuevo para multi-admin
    adminId?: string; // For pre-selecting admin when creating from Entity Management
  };
}

export const PanelModal = ({ open, onClose, mode, initialData }: PanelModalProps) => {
  const [form] = Form.useForm<PanelFormValues>();
  const { users, createPanel, editPanel } = useSuperAdmin();
  const [selectedAdmins, setSelectedAdmins] = useState<string[]>([]);
  const currentUserId = getAndSanitize('userId');

  // Resetear el formulario cuando cambia el modo o los datos iniciales
  useEffect(() => {
    if (mode === 'edit' && initialData) {
      // Extraer IDs de administradores del array users o usar userId si está disponible
      const adminIds = initialData.users 
        ? initialData.users.map(u => u.userId)
        : (initialData.userId ? [initialData.userId] : []);
      
      setSelectedAdmins(adminIds);
      
      form.setFieldsValue({
        ...initialData,
        admins: adminIds.map(userId => ({ userId }))
      });
    } else {
      form.resetFields();
      
      // Prioridad: adminId específico del Entity Management > usuario actual
      let preSelectedAdminId = null;
      if (initialData?.adminId) {
        // Pre-seleccionar admin específico cuando se crea desde Entity Management
        preSelectedAdminId = initialData.adminId;
      } else if (currentUserId) {
        // Fallback al usuario actual
        preSelectedAdminId = currentUserId;
      }
      
      if (preSelectedAdminId) {
        setSelectedAdmins([preSelectedAdminId]);
        form.setFieldsValue({ 
          admins: [{ userId: preSelectedAdminId }] 
        });
      } else {
        setSelectedAdmins([]);
      }
    }
  }, [form, initialData, mode, currentUserId, open]);

  // Manejar cambios en la selección de administradores
  const handleAdminChange = (values: string[]) => {
    setSelectedAdmins(values);
    
    // También actualizar el campo admins en el formulario
    const formattedAdmins = values.map(userId => ({ userId }));
    form.setFieldsValue({ admins: formattedAdmins });
  };

  const handleSubmit = (values: any) => {
    // Asegurarnos de que selectedAdmins tenga contenido
    if (selectedAdmins.length === 0 && currentUserId) {
      const updatedAdmins = [currentUserId];
      setSelectedAdmins(updatedAdmins);
      
      // Asegurarse de que los valores a enviar también incluyan el admin
      values.admins = updatedAdmins.map(userId => ({ userId }));
    } else {
      // Asegurarnos que values.admins está correctamente formateado
      values.admins = selectedAdmins.map(userId => ({ userId }));
    }


    if (mode === 'edit' && initialData) {
      editPanel({
        id: initialData.id,
        data: values,
      });
    } else {
      createPanel(values);
    }
    
    onClose();
    form.resetFields();
  };

  return (
    <Modal
      title={`${mode === 'edit' ? 'Editar' : 'Crear'} Panel`}
      open={open}
      onCancel={onClose}
      footer={null}
      maskClosable={false}
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={
          mode === 'create' 
          ? { admins: currentUserId ? [{ userId: currentUserId }] : [] } 
          : undefined
        }
      >
        <Form.Item
          label="Nombre"
          name="name"
          rules={[{ required: true, message: 'Por favor ingrese el nombre' }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          label="Usuario"
          name="username"
          rules={[{ required: true, message: 'Por favor ingrese el usuario' }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          label="Contraseña"
          name="password"
          rules={[{ required: mode === 'create', message: 'Por favor ingrese la contraseña' }]}
        >
          <Input.Password 
            placeholder={mode === 'edit' ? "Dejar en blanco para mantener la contraseña actual" : "Ingrese la contraseña"}
          />
        </Form.Item>
        <Form.Item
          label="URL Base"
          name="baseUrl"
          rules={[{ required: true, message: 'Por favor ingrese la URL base' }]}
        >
          <Input />
        </Form.Item>
        
        <Divider orientation="left">Administradores</Divider>
        
        <Form.Item
          label="Seleccionar Administradores"
          name="admins"
          rules={[{ 
            required: true, 
            message: 'Por favor seleccione al menos un administrador',
            validator: (_, __) => {
              // Verificar que haya al menos un administrador seleccionado
              return selectedAdmins.length > 0 
                ? Promise.resolve() 
                : Promise.reject(new Error('Seleccione al menos un administrador'));
            }
          }]}
        >
          <Select
            mode="multiple"
            style={{ width: '100%' }}
            placeholder="Seleccione uno o más administradores"
            value={selectedAdmins}
            onChange={handleAdminChange}
            optionLabelProp="label"
          >
            {users
              .filter(u => ['ADMIN', 'DISTRIBUITOR'].includes(u.role))
              .map(user => (
                <Select.Option 
                  key={user.id} 
                  value={user.id}
                  label={`${user.username} (${user.role})`}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>{user.username}</span>
                    <span style={{ color: '#999' }}>{user.role}</span>
                  </div>
                </Select.Option>
              ))
            }
          </Select>
        </Form.Item>
        
        <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
          <Space>
            <Button onClick={onClose}>
              Cancelar
            </Button>
            <Button 
              type="primary" 
              htmlType="submit"
              disabled={selectedAdmins.length === 0}
            >
              {mode === 'create' ? 'Crear' : 'Actualizar'}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};